import React from 'react';
import { Compass, Link2, Wrench, Blocks, Mountain } from 'lucide-react';
export const FLYWHEEL = [{
  name: 'Discover',
  icon: Compass,
  angle: 0,
  blurb: 'Find remarkable founders building from nature.'
}, {
  name: 'Connect',
  icon: Link2,
  angle: 72,
  blurb: 'Bring together the right people, knowledge and technology.'
}, {
  name: 'Commercialization',
  icon: Wrench,
  angle: 144,
  blurb: 'Turn promising ideas into real-world applications.'
}, {
  name: 'Combine',
  icon: Blocks,
  angle: 216,
  blurb: 'Connect innovations to unlock new possibilities.',
  pivot: true
}, {
  name: 'Scale',
  icon: Mountain,
  angle: 288,
  blurb: 'Help what works reach the world.'
}];
function polar(angleDeg: number, r: number, cx = 200, cy = 200) {
  const a = (angleDeg - 90) * Math.PI / 180;
  const round = (v: number) => Math.round(v * 100) / 100;
  return [round(cx + r * Math.cos(a)), round(cy + r * Math.sin(a))];
}
export function FlywheelDiagram() {
  const R = 150;
  const gap = 16;
  const arcs = FLYWHEEL.map((n, i) => {
    const next = FLYWHEEL[(i + 1) % FLYWHEEL.length];
    const a0 = n.angle + gap;
    const a1 = (next.angle - gap + 360) % 360;
    const [x0, y0] = polar(a0, R);
    const [x1, y1] = polar(a1, R);
    return `M ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1}`;
  });
  return <div className="relative mx-auto aspect-square w-full max-w-[19rem] sm:max-w-md" role="img" aria-label="The Vitality innovation flywheel: Discover, Connect, Commercialization, Combine, Scale, arranged in a repeating cycle" data-id="element-130" data-mp="1gyeqmw535uvp">
      <svg viewBox="-30 -30 460 460" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-131" data-mp="29nc6ytpqlhlt">
        <defs data-id="element-132" data-mp="24u10wusr14uy">
          <marker id="fw-arrow" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse" data-id="element-133" data-mp="24thf32zz9wbn">
            <path d="M 0 1 L 9 5 L 0 9" fill="none" stroke="currentColor" strokeWidth="1.6" data-id="element-134" data-mp="2skwmvbaix5ua" />
          </marker>
        </defs>
        <g fill="none" stroke="var(--forest-mid)" strokeOpacity={0.7} strokeWidth="1.2" color="var(--forest-mid)" data-id="element-135" data-mp="qoxlj5v7m80h">
          {arcs.map((d, i) => <path key={i} d={d} markerEnd="url(#fw-arrow)" data-id="element-136" data-mp="gy2uzpaxkm98" data-mpi={i} />)}
        </g>
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center" aria-hidden="true" data-id="element-137" data-mp="gw8i8y9tc0g8">
        <p className="text-[8px] uppercase tracking-[0.2em] text-foreground/70 sm:text-[10px] sm:tracking-[0.28em]" data-id="element-138" data-mp="7x4yrt6qhj17">
          The Vitality
          <br data-id="element-139" data-mp="c0b14pqq6q2e" />
          innovation flywheel
        </p>
        <h3 className="mt-2 max-w-[62%] font-serif text-2xl leading-[1.1] sm:mt-4 sm:max-w-none sm:text-4xl" data-id="element-140" data-mp="3u5efl12l0efh">
          From
          <br data-id="element-141" data-mp="2ytq3ls6dekk" />
          breakthrough
          <br data-id="element-142" data-mp="1sd8bbw3zijbb" />
          to impact.
        </h3>
      </div>

      {FLYWHEEL.map(n => {
      const [x, y] = polar(n.angle, 41, 50, 50);
      const Icon = n.icon;
      const below = n.angle === 144 || n.angle === 216;
      const above = n.angle === 0;
      return <div key={n.name} className="absolute -translate-x-1/2 -translate-y-1/2" style={{
        left: `${x}%`,
        top: `${y}%`
      }} aria-hidden="true" data-id="element-143" data-mp="3hs0vcdhovnk2">
            {/* h-/w- pairs rather than size-*, which only exists in Tailwind
                3.4 and later. The bundler's version is not pinned here. */}
            <div className={`flex h-11 w-11 items-center justify-center rounded-full border-2 bg-background sm:h-14 sm:w-14 ${n.pivot ? 'border-accent text-accent' : 'border-forest-mid text-forest-mid'}`} data-id="element-144" data-mp="4efk6wvbwbv4">
              <Icon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} data-id="element-145" data-mp="9etnk1lzixmt" />
            </div>
            {/* FIXED: this label was text-gold, which is 2.69:1 on cream and
                fails AA at every size. Now accent-text (#8D6F29, 4.53:1). */}
            <p className={`absolute whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.15em] sm:text-[10px] sm:tracking-[0.2em] ${n.pivot ? 'text-accent-text' : 'text-foreground/80'} ${above ? 'bottom-full left-1/2 mb-1 -translate-x-1/2' : 'top-full left-1/2 mt-1 -translate-x-1/2'} ${!above && !below ? n.angle < 180 ? 'lg:left-full lg:top-1/2 lg:ml-2 lg:mt-0 lg:-translate-y-1/2 lg:translate-x-0' : 'lg:left-auto lg:right-full lg:top-1/2 lg:mr-2 lg:mt-0 lg:-translate-y-1/2 lg:translate-x-0' : ''}`} data-id="element-146" data-mp="2b7htwjtxe16t">
              {n.name}
            </p>
          </div>;
    })}
    </div>;
}